<template>
  <Teleport to="body">
    <TransitionGroup
      name="toast"
      tag="div"
      class="nvx-toast-container"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="nvx-toast"
        :class="[`nvx-toast--${toast.type}`]"
      >
        <div class="nvx-toast__icon">
          <NvxIcon :name="getIcon(toast.type)" size="sm" />
        </div>
        <div class="nvx-toast__content">
          <div class="nvx-toast__title">{{ toast.title }}</div>
          <div v-if="toast.message" class="nvx-toast__message">
            {{ toast.message }}
          </div>
        </div>
        <button
          v-if="toast.dismissible"
          class="nvx-toast__close"
          @click="$emit('dismiss', toast.id)"
        >
          <NvxIcon name="x" size="sm" />
        </button>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup lang="ts">
import NvxIcon from './NvxIcon.vue'

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  dismissible?: boolean
}

defineProps<{
  toasts: Toast[]
}>()

defineEmits<{
  (e: 'dismiss', id: string): void
}>()

function getIcon(type: string): string {
  switch (type) {
    case 'success':
      return 'check-circle'
    case 'error':
      return 'x-circle'
    case 'warning':
      return 'alert-triangle'
    case 'info':
    default:
      return 'info'
  }
}
</script>

<style lang="scss" scoped>
.nvx-toast-container {
  position: fixed;
  top: var(--nvx-spacing-4);
  right: var(--nvx-spacing-4);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: var(--nvx-spacing-3);
  max-width: 400px;
}

.nvx-toast {
  display: flex;
  align-items: flex-start;
  gap: var(--nvx-spacing-3);
  padding: var(--nvx-spacing-4);
  border-radius: var(--nvx-border-radius-md);
  background-color: var(--nvx-color-background-elevated);
  border: 1px solid var(--nvx-color-border-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 300px;

  &--success {
    border-left: 4px solid #10B981;

    .nvx-toast__icon {
      color: #10B981;
    }
  }

  &--error {
    border-left: 4px solid #EF4444;

    .nvx-toast__icon {
      color: #EF4444;
    }
  }

  &--warning {
    border-left: 4px solid #F59E0B;

    .nvx-toast__icon {
      color: #F59E0B;
    }
  }

  &--info {
    border-left: 4px solid var(--nvx-color-primary);

    .nvx-toast__icon {
      color: var(--nvx-color-primary);
    }
  }

  &__icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: var(--nvx-font-size-sm);
    font-weight: var(--nvx-font-weight-semibold);
    color: var(--nvx-color-text-primary);
    line-height: 1.4;
  }

  &__message {
    margin-top: var(--nvx-spacing-1);
    font-size: var(--nvx-font-size-xs);
    color: var(--nvx-color-text-secondary);
    line-height: 1.5;
  }

  &__close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: var(--nvx-border-radius-sm);
    background: transparent;
    color: var(--nvx-color-text-tertiary);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: var(--nvx-color-background-tertiary);
      color: var(--nvx-color-text-primary);
    }
  }
}

// Transition animations
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
